import { useCallback, useEffect, useMemo } from "react"
import { useField } from "formik"
import { makeStyles } from "tss-react/mui"
import { Grid } from "@mui/material"
import { MainOrderFormikRef, MeetingServiceTabValues } from "../components/form/formTypes"
import { MeetingServicePriceListState } from "../createOrderTypes"
import { MeetingServiceOrder } from "../../../../modules/merchant/accounting/accounting/accountingTypes"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { ButtonConfigType } from "../../filters/orderTypeFilter/StandardOrderTypeFilter"
import { CreateOrderDialogTitleWrap } from "../components/CreateOrderDialogTitleWrap"
import {
    ResourceBookingDraftProvider,
    useResourceBookingDraft,
} from "../components/resource/ResourceBookingDraftContext"
import {
    ResourceBookingConfirmDialogProvider,
    useConfirmBookingDialogActions,
} from "../components/resource/ResourceBookingConfirmDialogContext"
import { SelectPriceLists } from "../components/shop/priceLists/SelectPriceLists"
import { CREATE_ORDER_TOP_BAR_HEIGHT } from "../components/CreateOrderDialogTitle"
import { useConfirmAbandonDialog } from "../hooks/useConfirmAbandonDialog"
import { AbandonBookingDialog } from "../components/AbandonBookingDialog"
import { ConfigErrorBoundary } from "../components/ConfigErrorBoundary"
import { AnimatedTabWrapper } from "../components/AnimatedTabWrapper"
import { useCreateOrderDialogType } from "../hooks/useCreateOrderDialogType"
import { useSelectPriceLists } from "../hooks/useSelectPriceLists"
import { OrderFormStatus } from "./helpers"
import { CreateOrderType, useOrderCreation } from "../helpers"
import { useCreateOrderForm, useCreateOrderFormConfig } from "./CreateOrderFormContext"
import { buildTabContent } from "./TabContent"
import { defaultPriceList } from "../defaults"
import { RefreshCalendarProvider } from "../components/resource/context/RefreshCalendarProvider"

const useStyles = makeStyles()(() => ({
    dialogContent: {
        display: "flex",
        flexDirection: "column",
        position: "relative",
        zIndex: 1,
        width: "100%",
        minHeight: `calc(100vh - ${CREATE_ORDER_TOP_BAR_HEIGHT})`,
    },
    content: {
        flexGrow: 1,
        position: "relative",
    },
    contentSlide: {
        height: "100%",
        overflow: "auto",
    },
}))

type OrderFormProps = Readonly<{
    filteredTabButtonConfig: ButtonConfigType<CreateOrderType>[]
    initialOrderType: CreateOrderType
    newOrderType: CreateOrderType | null
    orderDetails?: MeetingServiceOrder
    orderFormStatusRef: React.MutableRefObject<OrderFormStatus>
    handleSubmitMeetingService: (values: MeetingServiceTabValues) => void
    outerFormik: MainOrderFormikRef
}>

function OrderFormContent({
    filteredTabButtonConfig,
    initialOrderType,
    newOrderType,
    orderDetails,
    orderFormStatusRef,
    handleSubmitMeetingService,
    outerFormik,
}: OrderFormProps) {
    const { user, handleClose, dialogType, orderContext, flowConfig } = useCreateOrderForm()
    const { orderType, setOrderType } = useOrderCreation(initialOrderType)
    const { isRefundOrder, isRegularOrder, isSpecialOrder } = useCreateOrderDialogType(dialogType)
    const { classes } = useStyles()

    const { draftEvent, initialEventStateId, isUpdatedOrder, syncDraftToFormik, handleCancelClick } =
        useResourceBookingDraft()
    const [{ value: priceListValue }, , priceListHelpers] = useField<MeetingServicePriceListState>("priceList")
    const { item: priceList } = priceListValue

    const { setConfirmBookingDialog } = useConfirmBookingDialogActions()

    const handleOpenMeetingServiceTab = () => {
        setOrderType(CreateOrderType.MEETING_SERVICE)
    }
    const { showSelectPriceListsDialog, setShowSelectPriceListsDialog, handleSelectPriceList } =
        useSelectPriceLists(handleOpenMeetingServiceTab)

    const { confirmAbandonDialogState, handleOpenConfirmAbandonDialog, handleCloseConfirmAbandonDialog } =
        useConfirmAbandonDialog()

    useEffect(() => {
        if (orderType === CreateOrderType.MEETING_SERVICE && !priceList) {
            handleOpenSelectPriceListsDialog()
        }
    }, [orderType, priceList])

    useEffect(() => {
        if (newOrderType && newOrderType !== orderType) {
            handleChangeOrderType(newOrderType)
        }
    }, [orderType, newOrderType])

    const handleChangeOrderType = useCallback(
        (type: CreateOrderType) => {
            if (!isRegularOrder) return

            if (type === CreateOrderType.RESOURCE_BOOKING) {
                setOrderType(type)
                return
            }

            if (type === CreateOrderType.MEETING_SERVICE) {
                if (draftEvent && (!initialEventStateId || isUpdatedOrder)) {
                    handleOpenConfirmAbandonDialog()
                } else {
                    syncDraftToFormik()
                    if (priceList) {
                        handleOpenMeetingServiceTab()
                    } else {
                        handleOpenSelectPriceListsDialog()
                    }
                }
            }
        },
        [draftEvent, isUpdatedOrder, initialEventStateId, isRegularOrder, priceList, syncDraftToFormik],
    )

    const handleOpenSelectPriceListsDialog = useCallback(() => {
        if (isRefundOrder) {
            return
        }
        setShowSelectPriceListsDialog(true)
        priceListHelpers.setValue(defaultPriceList)
    }, [setShowSelectPriceListsDialog, isRefundOrder])

    const tabs = useMemo(
        () =>
            buildTabContent({
                handleChangeOrderType,
                orderFormStatusRef,
                handleOpenSelectPriceListsDialog,
                orderDetails,
                handleSubmitMeetingService,
                outerFormik,
                isSpecialOrder,
                contentSlideClassName: classes.contentSlide,
            }),
        [
            handleChangeOrderType,
            orderFormStatusRef,
            handleOpenSelectPriceListsDialog,
            orderDetails,
            handleSubmitMeetingService,
            outerFormik,
            isSpecialOrder,
            classes.contentSlide,
        ],
    )

    const hasResources = !!draftEvent && draftEvent.resourceIds.length > 0
    const handleConfirmAbandonDialogClick = () => {
        handleCloseConfirmAbandonDialog()
        setConfirmBookingDialog(true)
    }
    const handleCancelAbandonDialogClick = () => {
        handleCancelClick()
        handleCloseConfirmAbandonDialog()
        if (priceList) {
            handleOpenMeetingServiceTab()
        } else {
            handleOpenSelectPriceListsDialog()
        }
    }

    return (
        <>
            <CreateOrderDialogTitleWrap
                orderType={orderType}
                handleChangeOrderType={handleChangeOrderType}
                filteredTabButtonConfig={filteredTabButtonConfig}
                handleOpenSelectPriceListsDialog={handleOpenSelectPriceListsDialog}
                orderFormStatusRef={orderFormStatusRef}
            />
            <ConfigErrorBoundary onClose={handleClose} flowConfig={flowConfig}>
                <Grid container flexWrap="nowrap">
                    <Grid container className={classes.dialogContent}>
                        <Grid container className={classes.content}>
                            <AnimatedTabWrapper activeKey={orderType} tabs={tabs} animationDuration={0.5} />
                        </Grid>
                    </Grid>

                    {confirmAbandonDialogState && (
                        <LocalizedStrict
                            id={
                                hasResources
                                    ? "create-order-confirm-booking-confirm-dialog"
                                    : "create-order-confirm-booking-confirm-no-resources-dialog"
                            }
                            attrs={{ title: true, message: true, buttonLabelSubmit: true, buttonLabelCancel: true }}
                        >
                            <AbandonBookingDialog
                                title={hasResources ? "Confirm booking" : "Booking not complete"}
                                message={
                                    hasResources
                                        ? "Your booking has not been completed. Please confirm your booking before proceeding."
                                        : "You haven't selected any resources yet. If you leave now, your booking will not be completed."
                                }
                                handleSubmitClick={
                                    hasResources ? handleConfirmAbandonDialogClick : handleCloseConfirmAbandonDialog
                                }
                                handleCancelClick={handleCancelAbandonDialogClick}
                                buttonLabelSubmit={hasResources ? "Confirm" : "Select resources"}
                                buttonLabelCancel={hasResources ? "Cancel" : "Leave anyway"}
                                open={confirmAbandonDialogState}
                                onClose={handleCloseConfirmAbandonDialog}
                            />
                        </LocalizedStrict>
                    )}
                </Grid>
            </ConfigErrorBoundary>

            {!isRefundOrder && showSelectPriceListsDialog && (
                <SelectPriceLists
                    userId={user.userId}
                    onSelect={handleSelectPriceList}
                    handleCloseMainDialog={handleClose}
                    mode={orderContext.mode}
                    handleCloseSelectDialog={() => {
                        setShowSelectPriceListsDialog(false)
                    }}
                    orderType={orderType}
                />
            )}
        </>
    )
}

export function OrderForm(props: OrderFormProps) {
    const { flowConfig } = useCreateOrderFormConfig()
    return (
        <RefreshCalendarProvider>
            <ResourceBookingDraftProvider orderDetails={props.orderDetails} flow={flowConfig?.flow}>
                <ResourceBookingConfirmDialogProvider>
                    <OrderFormContent {...props} />
                </ResourceBookingConfirmDialogProvider>
            </ResourceBookingDraftProvider>
        </RefreshCalendarProvider>
    )
}
